// -----------------------------------------------------------------------
// Lightweight Google Analytics 4 wrapper — safe when gtag isn't loaded
// (ad blockers, dev environments) and typed enough that our components
// only need to call `track(...)`.
//
// GA4 measurement ID lives in `frontend/public/index.html`.
// Every event uses GA4 "recommended event" names where possible so that
// once you set up Google Ads conversions, they can be picked up
// automatically without renaming anything.
// -----------------------------------------------------------------------

export const GA_MEASUREMENT_ID = "G-T5DT9Q1TYV";

/** Is gtag actually loaded on the page? */
const gtagReady = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

/** Fire an event (safe when gtag is blocked / not loaded) */
export const track = (eventName, params = {}) => {
  if (!gtagReady()) return;
  try {
    window.gtag("event", eventName, params);
  } catch (_) {
    /* silent */
  }
};

/** Manual page_view for SPA route changes (GA doesn't auto-fire on pushState) */
export const trackPageView = (path, title) => {
  if (!gtagReady()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  } catch (_) {
    /* silent */
  }
};

/** Convenience wrappers for the events we actually fire */
export const analytics = {
  // ---------- Navigation ----------
  megaMenuOpen: () => track("mega_menu_open"),
  languageChange: (from, to) => track("language_change", { from, to }),
  themeToggle: (theme) => track("theme_toggle", { theme }),

  // ---------- Product discovery ----------
  productCardClick: (slug) =>
    track("select_item", { item_list_name: "solutions_grid", item_id: slug, item_name: `TRYB ${slug}` }),
  productPageView: (slug) =>
    track("view_item", { item_id: slug, item_name: `TRYB ${slug}` }),
  visitLiveProduct: (slug, url) =>
    track("outbound_click", { link_domain: safeDomain(url), link_url: url, item_id: slug }),
  walletBadgeClick: (slug, provider) =>
    track("wallet_badge_click", { item_id: slug, wallet_provider: provider }),

  // ---------- Contact form funnel ----------
  contactFormStart: (source) => track("form_start", { form_id: "contact", source }),
  contactFormFieldFocus: (fieldName) =>
    track("form_field_focus", { form_id: "contact", field: fieldName }),
  contactFormSubmit: (interest, country) =>
    // GA4 "generate_lead" is a recommended event; Google Ads picks it up as a
    // conversion candidate automatically. `value` and `currency` let us
    // attribute revenue to lead sources later.
    track("generate_lead", {
      form_id: "contact",
      lead_interest: interest,
      lead_country: country,
      currency: "USD",
      value: 25,
    }),
  contactFormError: (message) =>
    track("form_submit_error", { form_id: "contact", error: message?.slice(0, 120) }),

  // ---------- Outbound + CTAs ----------
  ctaClick: (label, location) => track("cta_click", { cta_label: label, location }),
  outboundClick: (url, label) =>
    track("outbound_click", { link_domain: safeDomain(url), link_url: url, link_label: label }),
  emailClick: (to) => track("contact", { method: "email", contact_target: to }),
  phoneClick: (number) => track("contact", { method: "phone", contact_target: number }),

  // ---------- Passive engagement ----------
  scrollDepth: (percent) => track("scroll", { percent_scrolled: percent }),
};

const safeDomain = (url) => {
  try { return new URL(url).host; } catch { return "unknown"; }
};

// -----------------------------------------------------------------------
// Scroll-depth tracker — one-shot events at 25/50/75/100%.
// Attach once from App.js.
// -----------------------------------------------------------------------
export const attachScrollDepthTracker = () => {
  if (typeof window === "undefined") return () => {};
  const fired = new Set();
  const thresholds = [25, 50, 75, 100];

  const onScroll = () => {
    const doc = document.documentElement;
    const total = doc.scrollHeight - doc.clientHeight;
    if (total <= 0) return;
    const percent = Math.min(100, Math.round((window.scrollY / total) * 100));
    for (const t of thresholds) {
      if (percent >= t && !fired.has(t)) {
        fired.add(t);
        analytics.scrollDepth(t);
      }
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
};
